import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Email, Send } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabase';
import { sendDailyReminderDigest, sendRenewalReminder } from '../utils/emailService';


const TestEmailReminder: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  // const { getMainCurrency } = useUserPreferences(user?.id);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: subscriptions } = useQuery(
    ['subscriptions', user?.id],
    async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('next_renewal_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!user,
    }
  );

  // Hide in production - only show in development
  // Can be overridden with REACT_APP_SHOW_TEST_FEATURES=true
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SHOW_TEST_FEATURES !== 'true') {
    return null;
  }

  if (!user) return null;

  const handleTestEmail = async () => {
    if (!user || !subscriptions) return;

    setIsSending(true);
    setResult(null);

    try {
      console.log('📧 Sending test email...');
      
      // Use the working email address for testing
      const success = await sendDailyReminderDigest(
        'hapis2002raihan@gmail.com', // Use the working email
        user.user_metadata?.full_name || user.user_metadata?.name,
        subscriptions,
        1 // Test with 1 day before (instead of 3)
      );

      if (success) {
        setResult({ success: true, message: '✅ Test email sent successfully!' });
      } else {
        setResult({ success: false, message: '❌ Failed to send test email' });
      }
    } catch (error) {
      console.error('❌ Error sending test email:', error);
      setResult({ 
        success: false, 
        message: `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setIsSending(false);
    }
  };

  // Force send test email regardless of timing
  const handleForceTestEmail = async () => {
    if (!user || !subscriptions || !subscriptions.length) return;

    setIsSending(true);
    setResult(null);

    try {
      console.log('📧 Force sending test email...');
      
      // Force send a reminder for the first subscription
      const subscription = subscriptions[0];
      const success = await sendRenewalReminder({
        subscription: subscription,
        daysUntilRenewal: 1,
        userEmail: 'hapis2002raihan@gmail.com',
        userName: user.user_metadata?.full_name || user.user_metadata?.name,
      });

      if (success) {
        setResult({ success: true, message: `✅ Force test email sent successfully for ${subscription.name}!` });
      } else {
        setResult({ success: false, message: '❌ Failed to send force test email' });
      }
    } catch (error) {
      console.error('❌ Error sending force test email:', error);
      setResult({ 
        success: false, 
        message: `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Email sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h6">Test Email Reminders</Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Test the email notification system by sending a sample reminder email to your registered email address.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong> {user.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Active Subscriptions:</strong> {subscriptions?.length || 0}
          </Typography>
          {subscriptions && subscriptions.length > 0 && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Subscription Due Dates:</strong>
              </Typography>
              {subscriptions.slice(0, 3).map((sub, index) => (
                <Typography key={sub.id} variant="caption" display="block" color="text.secondary">
                  • {sub.name}: {new Date(sub.next_renewal_date).toLocaleDateString()} 
                  ({Math.ceil((new Date(sub.next_renewal_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days away)
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        {result && (
          <Alert 
            severity={result.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {result.message}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={isSending ? <CircularProgress size={20} /> : <Send />}
          onClick={handleTestEmail}
          disabled={isSending || !subscriptions?.length}
          fullWidth
          sx={{
            color: theme.palette.mode === 'dark' ? 'black' : 'white',
            '&:hover': {
              color: theme.palette.mode === 'dark' ? 'black' : 'white',
            },
            '&:disabled': {
              color: theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.5)' 
                : 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {isSending ? 'Sending Test Email...' : 'Send Test Email (1 day before)'}
        </Button>

        <Button
          variant="outlined"
          startIcon={isSending ? <CircularProgress size={20} /> : <Send />}
          onClick={handleForceTestEmail}
          disabled={isSending || !subscriptions?.length}
          fullWidth
          sx={{ mt: 1 }}
        >
          {isSending ? 'Sending Force Test...' : 'Force Test Email (Any Subscription)'}
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          This will send a test email to {user.email} with your current subscriptions.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TestEmailReminder; 